import { Suspense } from 'react';
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AppShell } from '@/components/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { APP_BASE } from '@/lib/api-base';
import { lazyRoute } from '@/lib/chunk-reload';

/**
 * Routes are mounted under `<APP_BASE>/...` so the same build works whether
 * the SPA is served at `/app/` (local dev) or `/ukv-payroll/app/` (prod).
 *
 * Page components are loaded lazily so that hitting `/payroll-runs` doesn't
 * pull the IT-declarations editor or Form-10E print sheet into the initial
 * bundle. The Suspense fallback is a small centred spinner — Vite's vendor
 * chunks (MUI / TanStack / etc.) come down once and are reused across pages.
 *
 * Eagerly imported (always needed): AppShell, Login, ErrorBoundary,
 * Placeholder. Everything else: lazyRoute — React.lazy that reloads the page
 * onto the new build when a deploy has replaced the chunk (lib/chunk-reload).
 */

// Eager (small + always needed for first paint)
import { LoginPage } from '@/pages/Login';
import { SsoCallbackPage } from '@/pages/SsoCallback';
import { SsoSetupPage } from '@/pages/SsoSetup';
import { ProvisioningPage } from '@/pages/Provisioning';
import { PlatformConsolePage } from '@/pages/PlatformConsole';
import { DashboardPage } from '@/pages/Dashboard';

// Lazy chunks — keyed roughly by feature area so the dev tools "Network" tab
// shows meaningful names like `pages-payroll-runs-...js`.
// Dynamic collections engine. Four routes serve every runtime-defined entity
// there will ever be — the collection key is a URL parameter, so a new entity
// needs no route, no page component and no rebuild.
const CollectionsAdminPage  = lazyRoute(() => import('@/dynamic/CollectionsAdminPage').then((m) => ({ default: m.CollectionsAdminPage })));
const CollectionListPage    = lazyRoute(() => import('@/dynamic/CollectionListPage').then((m) => ({ default: m.CollectionListPage })));
const CollectionTrashPage   = lazyRoute(() => import('@/dynamic/CollectionTrashPage').then((m) => ({ default: m.CollectionTrashPage })));
const CollectionFormPage    = lazyRoute(() => import('@/dynamic/CollectionFormPage').then((m) => ({ default: m.CollectionFormPage })));
const CollectionDetailPage  = lazyRoute(() => import('@/dynamic/CollectionDetailPage').then((m) => ({ default: m.CollectionDetailPage })));
const MasterSetupPage       = lazyRoute(() => import('@/dynamic/setup/MasterSetupPage').then((m) => ({ default: m.MasterSetupPage })));
const EntitiesListPage      = lazyRoute(() => import('@/pages/EntitiesList').then((m) => ({ default: m.EntitiesListPage })));
const EmployeesList         = lazyRoute(() => import('@/pages/employees/List').then((m) => ({ default: m.EmployeesList })));
const EmployeeViewPage      = lazyRoute(() => import('@/pages/employees/View').then((m) => ({ default: m.EmployeeViewPage })));
const PayItemsList          = lazyRoute(() => import('@/pages/pay-items/List').then((m) => ({ default: m.PayItemsList })));
const StructuresList        = lazyRoute(() => import('@/pages/salary-structures/List').then((m) => ({ default: m.StructuresList })));
const StructureEditor       = lazyRoute(() => import('@/pages/salary-structures/Editor').then((m) => ({ default: m.StructureEditor })));
const GradeBandsList        = lazyRoute(() => import('@/pages/grade-bands/List').then((m) => ({ default: m.GradeBandsList })));
const PayrollConfigPage     = lazyRoute(() => import('@/pages/payroll-config/Page').then((m) => ({ default: m.PayrollConfigPage })));
const IntegrationHubPage    = lazyRoute(() => import('@/pages/integrations/Page').then((m) => ({ default: m.IntegrationHubPage })));
const PayrollRunsList       = lazyRoute(() => import('@/pages/payroll-runs/List').then((m) => ({ default: m.PayrollRunsList })));
const PayrollRunDetail      = lazyRoute(() => import('@/pages/payroll-runs/Detail').then((m) => ({ default: m.PayrollRunDetail })));
const HoldSalariesPage      = lazyRoute(() => import('@/pages/hold-salaries/Page').then((m) => ({ default: m.HoldSalariesPage })));
const ReportsPage           = lazyRoute(() => import('@/pages/reports/Page').then((m) => ({ default: m.ReportsPage })));
const ReportBuilderList     = lazyRoute(() => import('@/pages/report-builder/List').then((m) => ({ default: m.ReportBuilderList })));
const ReportBuilderEditor   = lazyRoute(() => import('@/pages/report-builder/Editor').then((m) => ({ default: m.ReportBuilderEditor })));
const ReportRunPage         = lazyRoute(() => import('@/pages/report-builder/RunPage').then((m) => ({ default: m.ReportRunPage })));
const DepartmentsList       = lazyRoute(() => import('@/pages/org-masters/DepartmentsList').then((m) => ({ default: m.DepartmentsList })));
const DesignationsList      = lazyRoute(() => import('@/pages/org-masters/DesignationsList').then((m) => ({ default: m.DesignationsList })));
const FlexiBenefitsPage     = lazyRoute(() => import('@/pages/flexi-benefits/Page').then((m) => ({ default: m.FlexiBenefitsPage })));
const ReimbursementsPage    = lazyRoute(() => import('@/pages/reimbursements/List').then((m) => ({ default: m.ReimbursementsListPage })));
const ITDeclarationsPage    = lazyRoute(() => import('@/pages/it-declarations/Page').then((m) => ({ default: m.ITDeclarationsPage })));
const ITDeclarationEditor   = lazyRoute(() => import('@/pages/it-declarations/Editor').then((m) => ({ default: m.ITDeclarationEditor })));
const TDSTaxPage            = lazyRoute(() => import('@/pages/tds/Page').then((m) => ({ default: m.TDSTaxPage })));
const FnFPage               = lazyRoute(() => import('@/pages/fnf/Page').then((m) => ({ default: m.FnFPage })));
const IncentivesPage        = lazyRoute(() => import('@/pages/incentives/Page').then((m) => ({ default: m.IncentivesPage })));
const AttendancePage        = lazyRoute(() => import('@/pages/attendance/Page').then((m) => ({ default: m.AttendancePage })));
const StatutoryReturnsPage  = lazyRoute(() => import('@/pages/statutory-returns/Page').then((m) => ({ default: m.StatutoryReturnsPage })));
const ChallanBatchesPage    = lazyRoute(() => import('@/pages/challan/Batches').then((m) => ({ default: m.ChallanBatchesPage })));
const ChallanEntriesPage    = lazyRoute(() => import('@/pages/challan/Entries').then((m) => ({ default: m.ChallanEntriesPage })));
const ChallanCompanyInfoPg  = lazyRoute(() => import('@/pages/challan/CompanyInfo').then((m) => ({ default: m.ChallanCompanyInfoPage })));
const EtdsReturnsPage       = lazyRoute(() => import('@/pages/challan/EtdsReturns').then((m) => ({ default: m.EtdsReturnsPage })));
const ChallanCertificatesPg = lazyRoute(() => import('@/pages/challan/Certificates').then((m) => ({ default: m.ChallanCertificatesPage })));
const RulesStudioPage       = lazyRoute(() => import('@/pages/rules/Page').then((m) => ({ default: m.RulesStudioPage })));
const StatutoryCompliancePage = lazyRoute(() => import('@/pages/statutory/Page').then((m) => ({ default: m.StatutoryCompliancePage })));
const PerquisitesPage         = lazyRoute(() => import('@/pages/perquisites/Page').then((m) => ({ default: m.PerquisitesPage })));
const LoansAdvancesPage       = lazyRoute(() => import('@/pages/loans/Page').then((m) => ({ default: m.LoansAdvancesPage })));
const EsopPage                = lazyRoute(() => import('@/pages/esop/Page').then((m) => ({ default: m.EsopPage })));
const SettingsPage            = lazyRoute(() => import('@/pages/settings/Page').then((m) => ({ default: m.SettingsPage })));
const NotificationsPage       = lazyRoute(() => import('@/pages/notifications/Page').then((m) => ({ default: m.NotificationsPage })));
const ApprovalsPage         = lazyRoute(() => import('@/pages/approvals/Page').then((m) => ({ default: m.ApprovalsPage })));
const WorkflowsPage         = lazyRoute(() => import('@/pages/workflows/Page').then((m) => ({ default: m.WorkflowsPage })));
const ArrearsListPage       = lazyRoute(() => import('@/pages/arrears/List').then((m) => ({ default: m.ArrearsListPage })));
const ArrearsDetailPage     = lazyRoute(() => import('@/pages/arrears/Detail').then((m) => ({ default: m.ArrearsDetailPage })));
const LopReversalsListPage  = lazyRoute(() => import('@/pages/lop-reversals/List').then((m) => ({ default: m.LopReversalsListPage })));
const UsersListPage         = lazyRoute(() => import('@/pages/users/List').then((m) => ({ default: m.UsersListPage })));
const AuditLogPage          = lazyRoute(() => import('@/pages/audit-log/Page').then((m) => ({ default: m.AuditLogPage })));
const ShiftsPage            = lazyRoute(() => import('@/pages/shifts/Page').then((m) => ({ default: m.ShiftsPage })));
const OtRulesPage           = lazyRoute(() => import('@/pages/ot-rules/Page').then((m) => ({ default: m.OtRulesPage })));
const OvertimePage          = lazyRoute(() => import('@/pages/overtime/Page').then((m) => ({ default: m.OvertimePage })));
const LeaveTypesPage        = lazyRoute(() => import('@/pages/leave-types/Page').then((m) => ({ default: m.LeaveTypesPage })));
const MyDashboard           = lazyRoute(() => import('@/pages/ess/Dashboard').then((m) => ({ default: m.MyDashboard })));
const MyPayslips            = lazyRoute(() => import('@/pages/ess/Payslips').then((m) => ({ default: m.MyPayslips })));
const MyDeclarations        = lazyRoute(() => import('@/pages/ess/Declarations').then((m) => ({ default: m.MyDeclarations })));
const MyFlexi               = lazyRoute(() => import('@/pages/ess/Flexi').then((m) => ({ default: m.MyFlexi })));
const MyAttendance          = lazyRoute(() => import('@/pages/ess/Attendance').then((m) => ({ default: m.MyAttendance })));
const EssLopReversalPage    = lazyRoute(() => import('@/pages/ess/LopReversal').then((m) => ({ default: m.EssLopReversalPage })));
const MyProfile             = lazyRoute(() => import('@/pages/ess/Profile').then((m) => ({ default: m.MyProfile })));
const MyLoansPage           = lazyRoute(() => import('@/pages/ess/Loans').then((m) => ({ default: m.MyLoansPage })));
const LoanRequestFormPage   = lazyRoute(() => import('@/pages/ess/LoanRequestForm').then((m) => ({ default: m.LoanRequestFormPage })));
const MySalary              = lazyRoute(() => import('@/pages/ess/Salary').then((m) => ({ default: m.MySalary })));
const SignupPage            = lazyRoute(() => import('@/pages/signup/SignupPage').then((m) => ({ default: m.SignupPage })));
const SignupVerifyPage      = lazyRoute(() => import('@/pages/signup/SignupVerifyPage').then((m) => ({ default: m.SignupVerifyPage })));
const SignupStatusPage      = lazyRoute(() => import('@/pages/signup/SignupStatusPage').then((m) => ({ default: m.SignupStatusPage })));

const PREFIX = APP_BASE || '/app';

/** Spinner shown while a lazy chunk is being fetched. */
function PageFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
      minHeight: 240, color: 'text.secondary' }}>
      <CircularProgress size={28} />
    </Box>
  );
}

/** Wrap a lazy element with Suspense + ErrorBoundary so any render error
 *  on that route stays scoped to the page rather than blanking the shell. */
function R(node: React.ReactNode) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageFallback />}>{node}</Suspense>
    </ErrorBoundary>
  );
}

function ToMasterSetup() {
  const { key } = useParams();
  return <Navigate to={`${PREFIX}/collections/${key}`} replace />;
}

export const router = createBrowserRouter(
  [
  // Root redirect — bare http://localhost:5173/ → /app/
  // Same in prod: /ukv-payroll/ would redirect → /ukv-payroll/app/, but that
  // path is handled by FastAPI's existing `/` redirect, not React Router.
  {
    path: '/',
    element: <Navigate to={`${PREFIX}/`} replace />,
  },
  {
    path: `${PREFIX}/login`,
    element: <ErrorBoundary><LoginPage /></ErrorBoundary>,
  },
  {
    path: `${PREFIX}/sso-callback`,
    element: <ErrorBoundary><SsoCallbackPage /></ErrorBoundary>,
  },
  {
    // Workspace onboarding. Unauthenticated on purpose — a brand-new client
    // has no workspace to authenticate against yet, so the unguessable
    // request UUID in the query string is the only guard.
    path: `${PREFIX}/provisioning`,
    element: <ErrorBoundary><ProvisioningPage /></ErrorBoundary>,
  },
  {
    // Fleet operations. Behind its own credential store (platform_admins in
    // the Master DB), never the workspace sign-in. The path is configurable
    // per deployment — obscurity is not the control, the credential is, but
    // there is no reason to advertise where the console lives either.
    path: `${PREFIX}/${import.meta.env.VITE_PLATFORM_PATH || 'platform'}`,
    element: <ErrorBoundary><PlatformConsolePage /></ErrorBoundary>,
  },
  {
    // Self-service workspace signup. Unauthenticated on purpose — the person
    // signing up has no workspace yet. Nothing is created until the server's
    // email verification (and, in APPROVAL mode, an operator) allows it.
    path: `${PREFIX}/signup`,
    element: R(<SignupPage />),
  },
  {
    path: `${PREFIX}/signup/verify`,
    element: R(<SignupVerifyPage />),
  },
  {
    path: `${PREFIX}/signup/status`,
    element: R(<SignupStatusPage />),
  },
  {
    path: `${PREFIX}/sso-setup`,
    element: <ErrorBoundary><SsoSetupPage /></ErrorBoundary>,
  },
  {
    path: PREFIX,
    element: <AppShell />,
    children: [
      { index: true,                       element: R(<DashboardPage />) },
      { path: 'collections',               element: R(<CollectionsAdminPage />) },
      { path: 'c/:key',                    element: R(<CollectionListPage />) },
      { path: 'c/:key/trash',              element: R(<CollectionTrashPage />) },
      { path: 'c/:key/:id/view',           element: R(<CollectionDetailPage />) },
      { path: 'c/:key/:id',                element: R(<CollectionFormPage />) },
      { path: 'collections/:key',          element: R(<MasterSetupPage />) },
      // The form builder and Studio were folded into the setup page; old links land there.
      { path: 'builder/:key',              element: <ToMasterSetup /> },
      { path: 'studio/:key',               element: <ToMasterSetup /> },
      { path: 'entities',                  element: R(<EntitiesListPage />) },
      { path: 'employees',                 element: R(<EmployeesList />) },
      { path: 'employees/:id',             element: R(<EmployeeViewPage />) },
      { path: 'pay-items',                 element: R(<PayItemsList />) },
      { path: 'payroll-config',            element: R(<PayrollConfigPage />) },
      { path: 'salary-structures',         element: R(<StructuresList />) },
      { path: 'salary-structures/new',     element: R(<StructureEditor />) },
      { path: 'salary-structures/:id',     element: R(<StructureEditor />) },
      // Legacy alias for any bookmarks pointing at /salary-structure
      { path: 'salary-structure',          element: R(<StructuresList />) },
      { path: 'grade-bands',               element: R(<GradeBandsList />) },
      { path: 'payroll-runs',              element: R(<PayrollRunsList />) },
      { path: 'payroll-runs/:id',          element: R(<PayrollRunDetail />) },
      // The withheld-pay register. Its own route, not a tab on a run: money
      // held in one month is still owed in the next, and belongs to the
      // employee rather than to any single payroll period.
      { path: 'hold-salaries',             element: R(<HoldSalariesPage />) },
      { path: 'departments',               element: R(<DepartmentsList />) },
      { path: 'designations',              element: R(<DesignationsList />) },
      { path: 'flexi-benefits',            element: R(<FlexiBenefitsPage />) },
      { path: 'reimbursements',            element: R(<ReimbursementsPage />) },
      { path: 'it-declarations',           element: R(<ITDeclarationsPage />) },
      { path: 'it-declarations/:id',       element: R(<ITDeclarationEditor />) },
      { path: 'tds-tax',                   element: R(<TDSTaxPage />) },
      { path: 'fnf',                       element: R(<FnFPage />) },
      { path: 'approvals',                 element: R(<ApprovalsPage />) },
      { path: 'reports',                   element: R(<ReportsPage />) },
      // Dynamic report builder. /new before /:idOrSlug so "new" is the
      // create screen rather than a lookup for a report slugged "new".
      { path: 'report-builder',            element: R(<ReportBuilderList />) },
      { path: 'report-builder/new',        element: R(<ReportBuilderEditor />) },
      { path: 'report-builder/:idOrSlug/run', element: R(<ReportRunPage />) },
      { path: 'report-builder/:idOrSlug',  element: R(<ReportBuilderEditor />) },
      { path: 'workflows',                 element: R(<WorkflowsPage />) },
      { path: 'arrears',                   element: R(<ArrearsListPage />) },
      { path: 'arrears/:id',               element: R(<ArrearsDetailPage />) },
      { path: 'lop-reversals',             element: R(<LopReversalsListPage />) },
      { path: 'incentives',                element: R(<IncentivesPage />) },
      { path: 'attendance',                element: R(<AttendancePage />) },
      { path: 'statutory-returns',         element: R(<StatutoryReturnsPage />) },
      // ── Challan (eTDS) ──
      { path: 'challan',                   element: <Navigate to={`${PREFIX}/challan/batches`} replace /> },
      { path: 'challan/batches',            element: R(<ChallanBatchesPage />) },
      { path: 'challan/entries',            element: R(<ChallanEntriesPage />) },
      { path: 'challan/company-info',       element: R(<ChallanCompanyInfoPg />) },
      { path: 'challan/etds-returns',       element: R(<EtdsReturnsPage />) },
      { path: 'challan/certificates',       element: R(<ChallanCertificatesPg />) },
      { path: 'rules-studio',              element: R(<RulesStudioPage />) },
      // Legacy path aliases
      { path: 'fnf-settlement',            element: R(<FnFPage />) },
      { path: 'variable-pay-incentives',   element: R(<IncentivesPage />) },
      { path: 'variable-pay',              element: <Navigate to={`${PREFIX}/incentives`} replace /> },
      { path: 'attendance-payroll',        element: R(<AttendancePage />) },
      { path: 'form16-compliance',         element: R(<StatutoryReturnsPage />) },
      // Still placeholder (no backend MVP yet)
      { path: 'esop-management',           element: R(<EsopPage />) },
      { path: 'perquisites',               element: R(<PerquisitesPage />) },
      { path: 'loans-advances',            element: R(<LoansAdvancesPage />) },
      { path: 'integrations',              element: R(<IntegrationHubPage />) },
      { path: 'statutory',                 element: R(<StatutoryCompliancePage />) },
      { path: 'users',                     element: R(<UsersListPage />) },
      { path: 'audit-log',                 element: R(<AuditLogPage />) },
      { path: 'shifts',                    element: R(<ShiftsPage />) },
      { path: 'ot-rules',                  element: R(<OtRulesPage />) },
      { path: 'overtime',                  element: R(<OvertimePage />) },
      { path: 'leave-types',               element: R(<LeaveTypesPage />) },
      // ── Employee Self-Service portal ──
      { path: 'me',                        element: R(<MyDashboard />) },
      { path: 'me/payslips',               element: R(<MyPayslips />) },
      { path: 'me/salary',                 element: R(<MySalary />) },
      { path: 'me/declarations',           element: R(<MyDeclarations />) },
      { path: 'me/declarations/:id',       element: R(<ITDeclarationEditor />) },
      { path: 'me/flexi',                  element: R(<MyFlexi />) },
      { path: 'me/reimbursements',         element: R(<ReimbursementsPage essMode />) },
      { path: 'me/attendance',             element: R(<MyAttendance />) },
      { path: 'me/lop-reversal',           element: R(<EssLopReversalPage />) },
      { path: 'me/loans',                  element: R(<MyLoansPage />) },
      { path: 'me/loans/new',              element: R(<LoanRequestFormPage />) },
      { path: 'me/loans/:id/edit',         element: R(<LoanRequestFormPage />) },
      { path: 'me/profile',                element: R(<MyProfile />) },
      // Notification history — under /me as well, because employees are
      // confined to /me (AppShell); the bell links to the one for the role.
      { path: 'me/notifications',          element: R(<NotificationsPage />) },
      { path: 'notifications',             element: R(<NotificationsPage />) },
      { path: 'settings',                  element: R(<SettingsPage />) },
      // Unknown route → redirect to dashboard so the URL corrects, instead of
      // silently rendering the dashboard at a bogus path (e.g. /variable-pay).
      { path: '*',                         element: <Navigate to={PREFIX} replace /> },
    ],
  },
  ],
  // On React Router v7 both former opt-ins (relative-splat-path resolution and
  // startTransition) are the built-in behaviour, so there is no `future` block
  // left to pass — we already ran on those semantics via the v6 future flags.
);
